import clsx from "clsx";
import { ArrowLeftRight, CheckCheck, Paperclip, Scale, Undo2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PendingTag } from "@/components/PendingTag";
import { CategoryIcon } from "@/components/ui/CategoryIcon";
import { Money } from "@/components/ui/Money";
import { formatDate } from "@/lib/format";
import type { Transaction } from "@/lib/types";

export function transactionTitle(t: Transaction): string {
  if (t.transaction_type === "transfer") return `${t.account.name} → ${t.counter_account?.name ?? ""}`;
  return t.merchant?.name || t.description || t.category?.name ||
    (t.transaction_type === "adjustment" ? "Balance adjustment" : "Transaction");
}

/** A transfer moves money between your own accounts: signed only from one account's view. */
export function displayAmount(t: Transaction, perspective?: string): { value: string; signed: boolean } {
  if (t.transaction_type === "transfer" && !perspective) return { value: t.amount, signed: false };
  return { value: t.signed_amount, signed: true };
}

export function secondaryText(t: Transaction): string | null {
  return t.description && t.description !== transactionTitle(t) ? t.description : null;
}

export function TypeIcon({ t }: { t: Transaction }) {
  const cls = "size-4";
  if (t.transaction_type === "transfer") return <ArrowLeftRight className={cls} aria-hidden />;
  if (t.transaction_type === "adjustment") return <Scale className={cls} aria-hidden />;
  if (t.transaction_type === "refund") return <Undo2 className={cls} aria-hidden />;
  return <CategoryIcon slug={t.category?.icon} className={cls} />;
}

/** Compact list row (dashboard, mobile). Links to the detail view. */
export function TransactionRow({ txn: t, perspective }: { txn: Transaction; perspective?: string }) {
  const subtitle = [
    t.transaction_type === "transfer" ? "Transfer" : t.category?.name,
    t.transaction_type !== "transfer" ? t.account.name : null,
    formatDate(t.transaction_date, true),
  ].filter(Boolean).join(" · ");
  return (
    <li className="border-t border-line first:border-t-0">
      <Link
        to={`/transactions?txn=${t.id}${perspective ? `&account_id=${perspective}` : ""}`}
        className={clsx("flex items-center gap-3 px-4 py-3 hover:bg-surface-2", t.is_voided && "opacity-60")}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-2 text-ink-2">
          <TypeIcon t={t} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={clsx("block truncate text-sm font-medium text-ink", t.is_voided && "line-through")}>
            {transactionTitle(t)}
          </span>
          <span className="block truncate text-xs text-ink-3">{subtitle}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end">
          <Money value={displayAmount(t, perspective).value} currency={t.currency}
                 signed={displayAmount(t, perspective).signed} tone={displayAmount(t, perspective).signed}
                 className="text-sm font-medium" />
          <span className="flex items-center gap-1 text-ink-3">
            {t.is_pending && <PendingTag />}
            {t.is_reconciled && <CheckCheck className="size-3.5" aria-label="Reconciled" />}
            {t.attachment_count > 0 && <Paperclip className="size-3.5" aria-label="Has attachment" />}
          </span>
        </span>
      </Link>
    </li>
  );
}
